/**
 * Who you are signed in as, always visible in the top bar.
 *
 * Two things it makes unmissable:
 *
 *  - the AUTHORITY the current session carries. A school admin, a teacher and a
 *    super admin see very different consequences from the same click, and
 *    "which account am I on" should never require opening a menu to answer.
 *  - the WORKSPACE, when there is more than one. The same person can be a
 *    teacher at a school and a student on their own account, and the shells look
 *    similar enough that the workspace is worth stating.
 *
 * Purely presentational. Every capability it hints at is enforced server-side.
 */
import { roleLabel, workspaceLabel } from '@/lib/auth';
import { useClaims } from '@/hooks/useSession';
import { fontPx } from '@/lib/fontSize';

/**
 * Colour by authority, using the existing palette rather than new tokens.
 *
 * Platform roles get the lime accent — deliberately the loudest, because that
 * is the session where a mistake reaches every school. Everything else is
 * quiet: this is an orientation cue, not a call to action.
 */
function toneFor(platformRole: string, role: string | null): { bg: string; fg: string } {
  if (platformRole !== 'USER') {
    return { bg: 'var(--brand-lime)', fg: 'var(--brand-forest)' };
  }
  if (role === 'SCHOOL_ADMIN') {
    return { bg: 'var(--bin-purple-bg)', fg: 'var(--bin-purple-deep)' };
  }
  if (role === 'TEACHER' || role === 'COUNSELLOR') {
    return { bg: 'var(--blue-bg)', fg: 'var(--blue-deep)' };
  }
  return { bg: 'var(--surface-sunken)', fg: 'var(--text-hint)' };
}

export function RoleBadge() {
  // Reactive, not the synchronous cache: the chrome renders in the same commit
  // as the guard that publishes that cache, so it would read null and never
  // re-render.
  const claims = useClaims();
  if (!claims) return null;

  const role = roleLabel(claims);
  const workspace = workspaceLabel(claims);
  const tone = toneFor(claims.platformRole, claims.role);

  return (
    <div className="flex items-center gap-2">
      {/* The development bypass must never be quiet. A session minted this way
          is acting as someone else, and anything typed into it lands on their
          real account. */}
      {claims.viaDevelopmentBypass && (
        <span
          title="This session was opened with the development bypass. You are acting as this user."
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            padding: '3px 9px',
            borderRadius: 999,
            fontSize: fontPx(11),
            fontWeight: 800,
            letterSpacing: '0.02em',
            background: 'var(--danger)',
            color: '#fff',
          }}
        >
          Bypass session
        </span>
      )}
      <span
        title={workspace ? `${role} · ${workspace}` : role}
        style={{
          display: 'inline-flex',
          alignItems: 'baseline',
          gap: 6,
          padding: '3px 10px',
          borderRadius: 999,
          fontSize: fontPx(11.5),
          fontWeight: 700,
          background: tone.bg,
          color: tone.fg,
          whiteSpace: 'nowrap',
          maxWidth: 260,
          overflow: 'hidden',
        }}
      >
        {role}
        {workspace && (
          <span
            className="hidden md:inline"
            style={{ fontWeight: 500, opacity: 0.75, overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            · {workspace}
          </span>
        )}
      </span>
    </div>
  );
}
